import ErrorType from '../../../Shared/Constants/ErrorType';
import Media     from '../Media/Media';

import BufferingType        from './Constants/BufferingType';
import PlaybackStatus       from './Constants/PlaybackStatus';
import TimecodeMode         from './Constants/TimecodeMode';
import IPlayback            from './Interfaces/IPlayback';
import getHumanReadableTime from './Util/getHumanReadableTime';

class Playback implements IPlayback {
    public playbackStatus: PlaybackStatus = PlaybackStatus.UNLOADED;
    public bufferingType: BufferingType = BufferingType.NONE;
    public currentTime: number = 0;
    public fatalErrorType: ErrorType = ErrorType.NONE;
    public minTime: number = 0;
    public maxTime: number = Infinity;
    public media: Media = null;
    public timecodeMode: string = TimecodeMode.DURATION;
    public hasPlayed: boolean = false;
    public resolution: string = '';
    public bitrate: number = -1;
    public estimatedBandwidth: number = -1;

    public static REWIND_AMOUNT = 30;
    public static FAST_FORWARD_AMOUNT = 30;
    public static SEEK_MARGIN_OF_ERROR = 0.2;
    public static VIDEO_EDGE_MARGIN_OF_ERROR = 7;

    public get hasMedia(): boolean {
        return this.media !== null;
    }

    public get isUnloaded(): boolean {
        return this.playbackStatus === PlaybackStatus.UNLOADED;
    }

    public get isLoading(): boolean {
        return (
            this.playbackStatus === PlaybackStatus.A_LOADING ||
            this.playbackStatus === PlaybackStatus.B_LOADING
        );
    }

    public get isPlaying(): boolean {
        return this.playbackStatus === PlaybackStatus.PLAYING;
    }

    public get isPaused(): boolean {
        return this.playbackStatus === PlaybackStatus.PAUSED;
    }

    public get isBuffering(): boolean {
        return this.playbackStatus === PlaybackStatus.BUFFERING;
    }

    public get isNetworkInducedBuffering(): boolean {
        return this.isBuffering && this.bufferingType === BufferingType.NETWORK;
    }

    public get isSeekInducedBuffering(): boolean {
        return this.isBuffering && this.bufferingType === BufferingType.SEEK;
    }

    public get hasEnded(): boolean {
        return this.playbackStatus === PlaybackStatus.ENDED;
    }

    public get isAtEndAndStopped(): boolean {
        return (
            this.hasEnded ||
            (
                this.hasMedia &&
                this.media.isVod &&
                this.playbackStatus === PlaybackStatus.PAUSED &&
                this.isAtVideoEdge
            )
        );
    }

    public get hasErrored(): boolean {
        return this.playbackStatus === PlaybackStatus.ERRORED;
    }

    public get isAtVideoEdge(): boolean {
        return this.adjustedCurrentTime >= this.duration - Playback.VIDEO_EDGE_MARGIN_OF_ERROR;
    }

    public get isAtLiveEdge(): boolean {
        return this.hasMedia && this.isAtVideoEdge && this.media.isLiveOrLinear;
    }

    public get duration(): number {
        return Math.max(0, this.maxTime - this.minTime);
    }

    public get adjustedCurrentTime(): number {
        return Math.max(0, this.currentTime - this.minTime);
    }

    public get isAtLeastOneHour(): boolean {
        return this.duration > 60 * 60;
    }

    public get isShowDurationMode(): boolean {
        return this.timecodeMode === TimecodeMode.DURATION;
    }

    public get isShowRemainingMode(): boolean {
        return this.timecodeMode === TimecodeMode.REMAINING;
    }

    public get timeRemaining(): number {
        return this.duration - this.currentTime;
    }

    public get humanReadableCurrentTime(): string {
        return getHumanReadableTime(this.adjustedCurrentTime, true, this.isAtLeastOneHour);
    }

    public get humanReadableDuration(): string {
        return getHumanReadableTime(this.duration, true, this.isAtLeastOneHour);
    }

    public get humanReadableTimeRemaining(): string {
        const prefix = this.timeRemaining > 0 ? '-' : '';

        return prefix + getHumanReadableTime(this.timeRemaining, true, this.isAtLeastOneHour);
    }

    public get humanReadableTimecode(): string {
        if (this.timecodeMode === TimecodeMode.REMAINING) {
            return this.humanReadableTimeRemaining;
        }

        return this.humanReadableDuration;
    }
}

export default Playback;